import { afterNextRender, effect, inject, Injectable, signal, untracked } from '@angular/core';
import { Dialog, type DialogRef } from '@angular/cdk/dialog';
import { ENV } from '@envs/environment';
import { LoadAnimation } from '@shared/load-animation/load-animation';
import type { LangLabel } from '@appTypes';
import { AILoadAnimation } from '@shared/ai-load-animation/ai-load-animation';

/* Servicio de estado general de la aplicación */

type Theme = 'light' | 'dark';

@Injectable({
  providedIn: 'root',
})
export class AppState {
  // Dependencias
  dialog = inject(Dialog);

  // Signals / atributos
  private asideInitValue = null;
  private asideSizeInitValue = '4.8rem';
  private loadAnimationSizeInitValue = '200px';

  private $_asideText = signal<string | string | null>(this.asideInitValue);
  private $_asideLabel = signal<LangLabel | null>(this.asideInitValue);
  private $_asideSize = signal<string>(this.asideSizeInitValue);
  private $_asideLogo = signal<boolean>(false);
  private $_loading = signal<boolean>(false);
  private $_loadingSize = signal<string>(this.loadAnimationSizeInitValue);
  private $_aiLoading = signal<boolean>(false);
  private $_theme = signal<Theme>('light');
  private _htmlDataset: DOMStringMap | null = null;
  private loadAnimationDialogRef: DialogRef<unknown, LoadAnimation> | null = null;
  private aiLoadAnimationDialogRef: DialogRef<unknown, AILoadAnimation> | null = null;

  // Constructor
  constructor() {
    // Mostrar el diálogo de animación de carga
    effect(() => {
      this.setLoadAnimationDialog(this.$loading());
    });

    // Mostrar el diálogo de animación de carga de IA
    effect(() => {
      this.setAILoadAnimationDialog(this.$aiLoading())
    })

    // Observar cambio de tema
    afterNextRender(() => {
      const html = document.documentElement;
      this.$_theme.set((html.dataset['theme'] as Theme) ?? 'light');
      const observer = new MutationObserver(() => {
        this.$_theme.set((html.dataset['theme'] as Theme) ?? 'light');
      });
      observer.observe(html, { attributes: true, attributeFilter: ['data-theme'] });
    });

    // Si ya existe el diálogo de carga con animación, relanzarlo con el nuevo tamaño
    effect(() => {
      this.$loadingSize();
      if (this.loadAnimationDialogRef && untracked(() => this.$loading())) {
        this.setLoadAnimationDialog(false);
        this.setLoadAnimationDialog(true);
      }
      else if (this.aiLoadAnimationDialogRef && untracked(() => this.$aiLoading())) {
        this.setAILoadAnimationDialog(false);
        this.setAILoadAnimationDialog(true);
      }
    });
  }

  // Getters / setters

  // Valor del archivo environment actual
  get ENV() {
    return ENV;
  }

  // Texto a mostrar en el lateral izquierdo
  get $asideText() {
    return this.$_asideText;
  }

  get $asideLabel() {
    return this.$_asideLabel;
  }

  // Tamaño del texto a mostrar
  get $asideSize() {
    return this.$_asideSize;
  }

  // Mostrar u ocultar el logo en el aside
  get $asideLogo() {
    return this.$_asideLogo;
  }

  // Estado de carga, para mostrar u ocultar la animación
  get $loading() {
    return this.$_loading;
  }

  // Estado de carga de IA, para mostrar u ocultar la animación
  get $aiLoading() {
    return this.$_aiLoading;
  }

  // Tamaño de la animación de carga
  get $loadingSize() {
    return this.$_loadingSize;
  }

  // Tema visual actual como signal de solo lectura
  get $theme() {
    return this.$_theme.asReadonly();
  }

  // Devuelve el dataset del elemento html
  private get htmlDataset(): DOMStringMap {
    if (!this._htmlDataset)
      this._htmlDataset = document.documentElement.dataset;
    return this._htmlDataset;
  }

  // Métodos

  // Establece el valor del signal de estado de carga en true
  loading() {
    this.$loading.set(true);
  }

  // Establece el valor del signal de estado de carga en false
  notLoading() {
    this.$loading.set(false);
    this.$aiLoading.set(false);
  }

  // Establece el valor del signal de estado de carga en true
  aiLoading() {
    this.$aiLoading.set(true);
  }

  // Muestra u oculta la animación de carga
  private setLoadAnimationDialog(visible: boolean) {
    if (visible && !this.loadAnimationDialogRef)
      this.loadAnimationDialogRef = this.dialog.open(LoadAnimation, {
        backdropClass: 'cdk-load-animation',
        disableClose: true,
        data: { size: this.$loadingSize() },
      });
    else if (!visible && this.loadAnimationDialogRef) {
      this.loadAnimationDialogRef.close();
      this.loadAnimationDialogRef = null;
    }
  }

  // Muestra u oculta la nimación de carga de IA
  private setAILoadAnimationDialog(visible: boolean) {
    if (visible && !this.aiLoadAnimationDialogRef) {
      this.aiLoadAnimationDialogRef = this.dialog.open(AILoadAnimation, {
        backdropClass: 'cdk-load-animation',
        disableClose: true,
        data: { scale: 148 },
      });
    }
    else if (!visible && this.aiLoadAnimationDialogRef) {
      this.aiLoadAnimationDialogRef.close();
      this.aiLoadAnimationDialogRef = null;
    }
  }

  // Restablece el estado original de los signals
  restore(): void {
    this.$_asideText.set(this.asideInitValue);
    this.$_asideLabel.set(this.asideInitValue);
    this.$_asideSize.set(this.asideSizeInitValue);
    this.$_loading.set(false);
    this.$_loadingSize.set(this.loadAnimationSizeInitValue);
  }

  // Establece el tema visual indicado
  setTheme(value: Theme): void {
    this.htmlDataset['theme'] = value;
    this.$_theme.set(value);
  }

  // Intercambia el tema visual
  toggleTheme() {
    this.setTheme(this.$theme() !== 'light' ? 'light' : 'dark');
  }
}
